import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type HTMLAttributes,
} from "react";
import { createPortal } from "react-dom";

export function PortalHost({
  id,
  ...rest
}: { id: string } & HTMLAttributes<HTMLDivElement>) {
  return <div id={id} {...rest} />;
}

function usePortalTarget(id: string) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    const current = document.getElementById(id) as HTMLElement | null;
    if (current) {
      setEl(current);
      return;
    }

    const obs = new MutationObserver(() => {
      const found = document.getElementById(id) as HTMLElement | null;
      if (found) {
        setEl(found);
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    observerRef.current = obs;

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [id]);

  return el;
}

export function Portal({ id, children }: { id: string; children: ReactNode }) {
  const target = usePortalTarget(id);
  if (!target) return null;
  return createPortal(children, target);
}
